/**
 * 共通型定義。
 * 仕様書: docs/01_spec/spec_common.md C-3
 *
 * 注: Firestore の Timestamp は Functions 側（firebase-admin）と admin-web 側（firebase/firestore）で
 *     異なる型を持つため、この shared パッケージでは `unknown` ではなく最小共通形で抽象化する。
 *     実際の Timestamp 変換は各 package 側のアダプタで行う。
 */

import type { AdminStatus, DepartmentId, Level, PermissionMap, Role } from '../constants';
import type { AccountingNotifyConfig } from './accounting-notify';
import type { BillingAddress, DeliveryAddress } from './mail';

/** Firestore Timestamp の最小抽象。実体は firebase-admin または firebase/firestore の Timestamp。 */
export type IsoOrTimestamp = string | { toDate(): Date; toMillis(): number };

// ─────────────────────────────────────────────
// admins / invitations
// ─────────────────────────────────────────────
export type UiTheme = 'dark' | 'light' | 'system';

export interface Admin {
  email: string;
  /** 後方互換: 旧 v1 ロール。新規コードは dept/level/isOwner を優先利用。 */
  role: Role;
  displayName: string;
  photoUrl: string | null;
  lastLoginAt: IsoOrTimestamp | null;
  uiTheme: UiTheme;
  sidebarCollapsed: boolean;
  createdAt: IsoOrTimestamp;

  // ─── v2: ユーザー管理拡張（仕様書: docs/01_spec/spec_user_management.md） ───
  /** 部署 ID。systemDepartments + カスタム追加分のいずれか。 */
  dept?: DepartmentId;
  /** 権限レベル。leader / employee / external。 */
  level?: Level;
  /** 最高権限フラグ（部署・レベルとは独立）。 */
  isOwner?: boolean;
  /** アカウント状態。pending=招待中 / active=ログイン完了。 */
  status?: AdminStatus;
  /** 表示用氏名（招待時に owner が入力した氏名）。displayName と並列。 */
  fullName?: string;
  /** ステータス遷移タイムスタンプ。 */
  invitedAt?: IsoOrTimestamp | null;
  activatedAt?: IsoOrTimestamp | null;

  // ─── v3.2.7 / T7: 個人プロフィール (請求先・納品先・振込口座) ───
  /**
   * 請求先住所 (複数登録可)。SA / 社員への報酬支払い時の宛先として使用。
   * `isDefault: true` のエントリは「既定の請求先」として優先利用される (1件のみ)。
   */
  billingAddresses?: BillingAddress[];
  /** 納品先住所 (複数登録可)。社内向け物品発送等に使用 (将来用)。 */
  deliveryAddresses?: DeliveryAddress[];
  /**
   * 振込口座 (複数登録可)。報酬支払い申請時に「★既定」を含む 1 件を選択する。
   * セキュリティ: 銀行情報は機微なため Firestore Rules で本人または owner のみ read 可。
   */
  bankAccounts?: BankAccount[];

  // ─── v3.2.14 / Phase 3a: SA 主導の請求書発行フロー ───
  /**
   * 適格請求書発行事業者登録番号 (インボイス制度対応、T + 13桁)。
   * SA / 法人取引先が発行する請求書に印字。
   * 例: "T1234567890123"
   * null / 未設定 = 免税事業者 (請求書では税抜のみ表示、消費税欄なし)
   */
  invoiceRegistrationNumber?: string | null;

  // ─── v3.2.30 / 外部SA管理: オンボーディング・契約ライフサイクル ───
  /**
   * 外部SA (dept='sales' && level='external') の登録〜活動可能までの進行状態。
   * 仮登録 → (同意済み + 入金済み) → 登録完了 → (受講済み) → 活動可能。
   * 社員・owner には付与されない (undefined)。
   */
  saOnboarding?: SaOnboarding;

  // ─── 掲示板 Phase 5: 個人別の掲示板通知チャネル設定 ───
  /**
   * 掲示板 (board_posts) の投稿・更新通知の受信チャネル設定。
   * - email: 通知メールを受信するか (既定 true)。
   * - googleChatWebhookUrl: 個人宛 Google Chat 通知先 webhook URL (未設定 = 通知なし)。
   * - chatworkRoomId: 個人宛 Chatwork 通知先ルーム ID (未設定 = 通知なし)。
   */
  boardNotify?: {
    email: boolean;
    googleChatWebhookUrl?: string | null;
    chatworkRoomId?: string | null;
  };
}

/** 外部SAのオンボーディング・ステータス (フラグから導出される 3 段階)。 */
export type SaOnboardingStatus = 'provisional' | 'registered' | 'active';

export const SA_ONBOARDING_STATUS_LABEL: Record<SaOnboardingStatus, string> = {
  provisional: '仮登録',
  registered: '登録完了',
  active: '活動可能',
};

/**
 * 外部SAのオンボーディング状態 (Admin.saOnboarding)。
 *
 * - 3 つの独立フラグ (consent/payment/briefing) は順不同で立つ。
 * - status はフラグから導出 (computeSaStatus)。サーバ側で保存し遷移検知・クエリに利用。
 * - 契約開始日は「登録完了 (consent + payment) になった月の翌月 1 日」を自動設定。
 *   手動上書き時は startDateManual=true。終了日 = 開始日 + 1年 − 1日 (自動導出)。
 * - 年次更新は前期終了日の翌日を新開始日とし contractTerm をインクリメント。
 */
export interface SaOnboarding {
  /** 導出ステータス (サーバ側で computeSaStatus を保存)。 */
  status: SaOnboardingStatus;

  /** 同意書サイン確認済み。 */
  consentConfirmed: boolean;
  consentConfirmedAt?: IsoOrTimestamp | null;
  /** 登録費の入金確認済み。 */
  paymentConfirmed: boolean;
  paymentConfirmedAt?: IsoOrTimestamp | null;
  /** SA 説明会の受講 (参加) 確認済み。 */
  briefingAttended: boolean;
  briefingAttendedAt?: IsoOrTimestamp | null;

  /** 申請日 (招待 invitedAt 由来。一覧の「申請日」列)。 */
  appliedAt?: IsoOrTimestamp | null;
  /** 「登録完了」に初めて遷移した日時 (契約開始日の算出根拠)。 */
  registeredAt?: IsoOrTimestamp | null;

  /** 契約開始日 'YYYY-MM-DD' (JST)。登録完了の翌月 1 日 or 手動指定。 */
  contractStartDate?: string | null;
  /** 契約終了日 'YYYY-MM-DD' (開始日 + 1年 − 1日)。 */
  contractEndDate?: string | null;
  /** true = 開始日を手動上書き済み (自動再計算しない)。 */
  startDateManual?: boolean;
  /** 契約期 (第 N 期。初回 = 1、年次更新でインクリメント)。 */
  contractTerm?: number;
  /** 契約年数 (この期の長さ)。未設定なら settings.saDefaultContractYears を使用。 */
  contractYears?: number;
  /** SA 本人が更新申請した日時 (SA ポータルの「更新」ボタン)。 */
  renewalRequestedAt?: IsoOrTimestamp | null;
  /** 更新リマインダーを送信済みの契約期 (重複通知防止)。 */
  renewalReminderSentForTerm?: number | null;
}

/**
 * 振込口座 (Admin.bankAccounts の要素、v3.2.7 / T7 追加)。
 */
export type BankAccountType = 'ordinary' | 'checking' | 'savings';

export const BANK_ACCOUNT_TYPE_LABELS: Record<BankAccountType, string> = {
  ordinary: '普通',
  checking: '当座',
  savings: '貯蓄',
};

export interface BankAccount {
  id: string;
  /** 表示用ラベル (例: "メイン", "副口座") */
  label?: string | null;
  bankName: string;
  branchName: string;
  /** 普通 / 当座 / 貯蓄 */
  accountType: BankAccountType;
  accountNumber: string;
  /** 口座名義 (カナ) */
  accountHolder: string;
  /** 既定の振込口座フラグ。true は配列内で最大 1 件まで。 */
  isDefault?: boolean;
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  email: string;
  /** 後方互換: 旧 v1 ロール。dept/level 優先。 */
  role: Role;
  invitedBy: string; // uid
  expiresAt: IsoOrTimestamp;
  status: InvitationStatus;
  acceptedAt: IsoOrTimestamp | null;
  createdAt: IsoOrTimestamp;

  // ─── v2: 追加フィールド ───
  /** 部署 ID。 */
  dept?: DepartmentId;
  /** 権限レベル。 */
  level?: Level;
  /** owner 招待フラグ（招待時点で isOwner=true で受諾）。 */
  isOwner?: boolean;
  /** 招待時に owner が入力した氏名。 */
  fullName?: string;
}

// ─────────────────────────────────────────────
// v2: 部署 / ロール権限マトリックス
// ─────────────────────────────────────────────

/** 部署マスタ（departments コレクション）。 */
export interface Department {
  /** 表示名。 */
  name: string;
  /** システム既定部署（admin/sales/marketing）は true、削除不可。 */
  isSystem: boolean;
  /** UI 並び順。昇順。 */
  order: number;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/** 部署×レベルごとの機能アクセスマトリックス（rolePermissions コレクション）。 */
export interface RolePermissions {
  dept: DepartmentId;
  level: Level;
  /** featureId → {view, create, edit, delete} のマップ。 */
  permissions: PermissionMap;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

// ─────────────────────────────────────────────
// settings
// ─────────────────────────────────────────────
/**
 * 自社の会社情報 (v3.3.x)。
 * 見積書・請求書の発行元、メール署名フッター、発注書などが参照する共通プロフィール。
 *
 * 設定場所:
 *   - デフォルト: settings/general.companyInfo
 *   - カテゴリー別上書き: product_categories/{id}.companyInfo
 * 解決は functions/src/common/companyInfo.ts の resolveCompanyInfo() に集約。
 */
export interface CompanyInfo {
  /** 会社名 (例: 株式会社カウデザイン)。 */
  name: string;
  /** 代表者名。 */
  representative: string;
  /** 郵便番号 (ハイフン込み, 例: 107-0062)。 */
  postalCode: string;
  /** 住所 (都道府県〜建物名まで)。 */
  address: string;
  /** 電話番号。 */
  phone: string;
  /** メールアドレス。 */
  email: string;
}

export interface SettingsGeneral {
  companyName: string;
  /**
   * 自社の会社情報 (v3.3.x)。請求書・見積書・メール署名等の発行元として参照される
   * デフォルト値。`name` は companyName とミラー保存される (後方互換)。
   * カテゴリー側に companyInfo があればそちらが優先される。
   */
  companyInfo?: CompanyInfo;
  privacyPolicyUrl: string;
  tokushoPolicyUrl: string | null;
  brandColorPrimary: string;
  brandColorSecondary: string;
  logoUrl: string | null;
  claudeModel: string;
  /**
   * LINE Bot のモデル・ルーティング戦略。
   * - "haiku": 全 round で Haiku（最速）
   * - "sonnet": 全 round で Sonnet（高品質）
   * - "router": round 0 を Haiku、それ以降を Sonnet（バランス型、推奨）
   * - 未設定: 後方互換で claudeModel を使用
   */
  routingMode?: 'haiku' | 'sonnet' | 'router';
  sessionTimeoutMinutes: number;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * 営業設定 (settings/sales)。
 *
 * 営業・取引に関わる横断的な設定をまとめる。
 * - quoteValidDays: 見積書の有効期限の既定日数 (issuedAt + N日)
 * - invoiceDueDays: 請求書の支払期限の既定日数 (issuedAt + N日)
 * - autoMailFromAccountId: 自動メール送信時のデフォルト送信元 (mail_accounts/{id})
 * - saAggregationDay: 販売代理店 (Sales Agent) の月次集計締め日 (1〜31)
 * - saPaymentCycle: 販売代理店への支払いサイクル
 *
 * SettingsGeneral とは独立。SettingsGeneral は会社情報・LINE Bot などの基盤設定、
 * SettingsSales は営業オペレーションの慣習値を扱う。
 */
export type SaPaymentCycle =
  /** 月末締・翌月末払 */
  | 'eom_next_eom'
  /** 月末締・翌々月末払 */
  | 'eom_2nd_next_eom'
  /** 20日締・翌月末払 */
  | 'd20_next_eom'
  /** 20日締・翌々月10日払 */
  | 'd20_2nd_10';

export interface SettingsSales {
  /** 見積有効期限の既定日数 (issuedAt + N日)。デフォルト 30。 */
  quoteValidDays: number;
  /** 請求支払期限の既定日数 (issuedAt + N日)。デフォルト 30。 */
  invoiceDueDays: number;
  /**
   * 自動メール送信時のデフォルト送信元 mail_accounts/{id}。
   * 未設定 = 業務メールテンプレート個別の fromAccountId / global default を使う。
   */
  autoMailFromAccountId: string | null;
  /**
   * 経理担当のメールアドレス。
   * 送金申請等の社内向け業務メールの宛先として使用する (v3.1.6 追加)。
   * 未設定 = 機能が利用できない (UI で警告表示)。
   */
  accountingEmail?: string | null;
  /**
   * 経理担当の Google Chat (chat_endpoints/{id}, kind=google_chat)。2026-10-26 追加。
   * accountingNotify で GC がオンのトリガーの送信先。
   */
  accountingGoogleChatEndpointId?: string | null;
  /**
   * 経理担当の Chatwork (chat_endpoints/{id}, kind=chatwork)。2026-10-26 追加。
   * accountingNotify で CW がオンのトリガーの送信先。
   */
  accountingChatworkEndpointId?: string | null;
  /**
   * 経理担当への通知を、トリガーごとに メール / Google Chat / Chatwork のどれで送るか。
   * 2026-10-26 追加。未設定のトリガーはメールのみ (resolveAccountingNotifyChannels)。
   */
  accountingNotify?: AccountingNotifyConfig;
  /**
   * 販売代理店 (Sales Agent) の月次売上集計の締め日 (1〜31)。
   * 例: 31 = 月末締。28 = 月末扱い (28〜31 を末日として扱う)。
   */
  saAggregationDay: number;
  /**
   * 販売代理店への支払いサイクル。
   */
  saPaymentCycle: SaPaymentCycle;
  /**
   * SA リファラル経由 (`?sa=`) で生まれた Deal の `assigneeUid` 既定値。
   * 設定されている場合、SA 経由 Deal 作成時に自動で社内担当者をアサインする。
   * 未設定 (null) は Deal.assigneeUid=null となり、UI で「未アサイン」として手動割当が必要。
   * 想定: 営業事務担当の admin uid を指定。
   */
  defaultSalesAssigneeUid?: string | null;
  /**
   * 現在の季節モード (v3.2.23+)。'normal' / 'sale' のワンクリック切替。
   * 商品カタログ・報酬セットの effective を全ブランド一斉に切り替える。
   * 未設定 (undefined / null) は 'normal' として扱う（後方互換）。
   */
  seasonMode?: 'normal' | 'sale';

  // ─── v3.2.30 / 外部SA 契約・更新 ───
  /** SA 新規登録料 (円・税込)。sa_provisional メールの {{fee}}。既定 55000。 */
  saRegistrationFee?: number;
  /** SA 更新料 (円・税込)。sa_renewal_reminder メールの {{fee}}。既定 33000。 */
  saRenewalFee?: number;
  /** SA 既定契約年数 (新規登録時の初期値)。既定 1。 */
  saDefaultContractYears?: number;
  /** 契約更新リマインダーを送る「終了日の何日前」。既定 30。 */
  saRenewalReminderDays?: number;

  // ─── 2026-07-28 / SA 顧客保有期間 ───
  /**
   * SA が獲得した顧客を「その SA の顧客」とみなす期間 (ヶ月)。既定 12。
   * 起算は SA への紐付け時、および その SA の商談で入金があった時点。
   * 期限を過ぎると mail_contacts.ownerSaUid が外れて弊社顧客になる。
   * 0 = 無期限 (この機能を実質 OFF にする)。
   */
  saCustomerRetentionMonths?: number;
  /** 顧客保有期限の「何日前」に SA へリマインダーを送るか。既定 30。0 = 送らない。 */
  saRetentionReminderDays?: number;

  // ─── 2026-08-07 / フォーム通知 ───
  /**
   * 返答待ち回答 (返答要求チェックボックス = TRUE) の通知先。
   * 未設定 (undefined) は 'board' として扱う (機能追加時の既定挙動)。
   */
  formReplyNotifyChannel?: FormReplyNotifyChannel;

  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * 返答待ち回答の通知先 (2026-08-07)。
 * - 'board' : 掲示板へ自動投稿
 * - 'mail'  : メール通知のみ (件名・本文は掲示板投稿と同一内容)
 * - 'both'  : 両方
 * - 'none'  : 通知しない (回収一覧の「返答待ち」ラベルは従来どおり付く)
 */
export type FormReplyNotifyChannel = 'board' | 'mail' | 'both' | 'none';

export const FORM_REPLY_NOTIFY_CHANNEL_LABELS: Record<FormReplyNotifyChannel, string> = {
  board: '掲示板（自動投稿）',
  mail: 'メール',
  both: '両方',
  none: 'なし',
};

/** 未設定時の既定通知先。 */
export const FORM_REPLY_NOTIFY_CHANNEL_DEFAULT: FormReplyNotifyChannel = 'board';

export const SETTINGS_SALES_DEFAULTS: Pick<
  SettingsSales,
  | 'quoteValidDays'
  | 'invoiceDueDays'
  | 'autoMailFromAccountId'
  | 'accountingEmail'
  | 'saAggregationDay'
  | 'saPaymentCycle'
  | 'defaultSalesAssigneeUid'
  | 'seasonMode'
  | 'formReplyNotifyChannel'
> = {
  formReplyNotifyChannel: FORM_REPLY_NOTIFY_CHANNEL_DEFAULT,
  quoteValidDays: 30,
  invoiceDueDays: 30,
  autoMailFromAccountId: null,
  accountingEmail: null,
  saAggregationDay: 31,
  saPaymentCycle: 'eom_next_eom',
  defaultSalesAssigneeUid: null,
  seasonMode: 'normal',
};

export const SA_PAYMENT_CYCLE_LABELS: Record<SaPaymentCycle, string> = {
  eom_next_eom: '月末締・翌月末払',
  eom_2nd_next_eom: '月末締・翌々月末払',
  d20_next_eom: '20日締・翌月末払',
  d20_2nd_10: '20日締・翌々月10日払',
};

/**
 * 個別説明スケジュールの予約受付締切設定。**ユーザー単位** で保持する
 * (briefing_settings/{uid})。各ユーザー(SA/管理者)が自分の枠 (assigneeUid) に
 * 適用する締切を個別に設定する。
 * 「枠の日付の何日前まで予約受付をオープンにするか」を平日 / 休日 (土日) 別に保持。
 * - leadDays = 0: 枠当日まで受付 (締切なし、従来挙動)。
 * - leadDays = N: 枠の N 日前を最終受付日とし、それ以降は締切 (過去の枠 / 公開フォーム非表示)。
 */
export interface SettingsBriefing {
  /** 平日 (月〜金) の枠: 何日前まで受付オープン。 */
  weekdayLeadDays: number;
  /** 休日 (土日) の枠: 何日前まで受付オープン。 */
  holidayLeadDays: number;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export const SETTINGS_BRIEFING_DEFAULTS: Pick<
  SettingsBriefing,
  'weekdayLeadDays' | 'holidayLeadDays'
> = {
  weekdayLeadDays: 0,
  holidayLeadDays: 0,
};

export interface SettingsNotifications {
  gmailFromAddress: string;
  gmailDailySummaryEnabled: boolean;
  gmailDailySummaryTime: string; // "HH:MM"
  googleChatEnabled: boolean;
  notifyOnNewUser: boolean;
  notifyOnLowRating: boolean;
  notifyOnError: boolean;
  notifyOnDeliveryCodeRename: boolean;
  notifyOnMailSendFailure: boolean;
  /**
   * 保留トレイ (deal_merge_candidates) に新規候補が入ったとき Google Chat スペースへ通知する。
   * ★ バックエンド (common/notifications.ts の TYPE_TO_SETTING_KEY) では以前から
   *   参照されていたが型・UI に無く「未設定 = 常に ON」で止められなかった (2026-08-18 に露出)。
   */
  notifyOnMergeCandidate?: boolean;
  /** 停滞商談 (7日) の通知。notifyOnMergeCandidate と同じ経緯で 2026-08-18 に型・UI へ露出。 */
  notifyOnStaleDeal?: boolean;
  /**
   * 停滞商談とみなす日数 (notifyStaleDeals の lastTouchedAt しきい値)。
   * 未設定 = 既定 7 日。有効範囲は 1〜365 日で、範囲外は関数側でクランプされる。
   * ★ 商談は停滞しても失注へ自動遷移しない (失注は markDealLost の手動確定のみ) ため、
   *   この通知が放置商談を検知する唯一の経路。商材のリードタイムに合わせて調整する。
   */
  staleDealThresholdDays?: number;
  /**
   * 保留トレイの新規候補を「個人宛」に通知する設定 (2026-08-18)。
   * Google Chat スペース宛 (notifyOnMergeCandidate) とは独立。
   * 宛先個人のチャネルは admins/{uid}.boardNotify を流用する
   * (掲示板と同じ「その人の通知先」設定。個人 Google Chat webhook / Chatwork ルーム)。
   */
  mergeCandidateNotify?: {
    /** 宛先へメールを送る。 */
    email?: boolean;
    /** 宛先の個人 Google Chat / Chatwork へ送る (admins/{uid}.boardNotify)。 */
    personalChat?: boolean;
    /** 宛先の内部メンバー uid。空配列 / 未設定なら個人宛通知は行わない。 */
    recipientUids?: string[];
  };
  /**
   * 管理者向け通知メール（招待 / システム通知）の送信元 mail_account ID。
   * 未設定の場合は mail_accounts の isDefault=true → status=active 最初の 1 件 にフォールバック。
   * 仕様書: docs/01_spec/spec_user_management.md U-9
   */
  invitationFromAccountId?: string | null;
  /**
   * 管理者通知 (Google Chat) の送信先 chat_endpoints ドキュメント ID。
   * v3.3.3 以降、Secret Manager の GOOGLE_CHAT_WEBHOOK_URL は廃止し、
   * チャット管理タブで登録した webhook から 1 つ選択する形式に変更。
   * 未設定 (null) の場合は Google Chat 通知をスキップ。
   */
  googleChatNotifyEndpointId?: string | null;
  /**
   * 一括配信（大量送信）— 日跨ぎ分割。
   * ON の場合、送信元アカウントの 1 予約あたり上限 (mailRecipientCap) を超える宛先でも
   * 予約を拒否せず、その上限を「1 日分」とみなして超過分を翌日以降へ自動繰り越す。
   * 実装は executeOne で各宛先の Cloud Task scheduleTime を floor(index/cap) 日ずつ後ろ倒し。
   * 2 日目以降は初回 sendAt と同じ時刻に送信される。
   */
  mailBulkCrossDaySplitEnabled?: boolean;
  /**
   * 一括配信（大量送信）— SMTP リレー方式の既定送信元アカウント (mail_accounts/{id})。
   * 設定されていれば配信予約フォームの送信元の初期選択に使う。
   * null = 未設定（従来どおり isDefault=true のアカウントを既定選択）。
   */
  mailBulkRelayAccountId?: string | null;
  /**
   * 配信停止フォーム（フォーム管理>マーケティング）の forms ドキュメント ID。
   * - このフォームを送信すると、一致する mail_contacts に配信停止タグ（UNSUBSCRIBE_TAG）が付与される。
   * - 一括/予約配信の本文末尾に、このフォームへの配信停止リンク（受信者メール自動補完）が自動挿入される。
   * - これ以外のマーケティングフォーム送信時は配信停止タグが除去され、配信先として復活する。
   * null / 未設定 = 配信停止機能オフ（フッター挿入・タグ付与なし）。
   */
  unsubscribeFormId?: string | null;
  /**
   * 掲示板の「全体連絡」用ブロードキャストチャネル (掲示板 Phase 5)。
   * 個人別 (Admin.boardNotify) とは別に、全体宛の掲示板通知を送る共通チャネル。
   * - googleChatWebhookUrl: 全体連絡用 Google Chat 通知先 webhook URL (未設定 = 通知なし)。
   * - chatworkRoomId: 全体連絡用 Chatwork 通知先ルーム ID (未設定 = 通知なし)。
   */
  boardBroadcast?: {
    googleChatWebhookUrl?: string | null;
    chatworkRoomId?: string | null;
  };
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export interface SecretStatus {
  configured: boolean;
  updatedAt: IsoOrTimestamp | null;
  updatedBy: string | null;
  lastRotatedAt: IsoOrTimestamp | null;
}

export type SecretKey =
  | 'anthropicApiKey'
  | 'geminiApiKey'
  | 'lineChannelSecret'
  | 'lineChannelAccessToken'
  | 'gmailOAuthClientId'
  | 'gmailOAuthClientSecret'
  // v3.3.3: googleChatWebhookUrl は廃止。chat_endpoints を選択する方式へ移行。
  | 'recaptchaSiteKey'
  // SES 管理 API（検証ステータス照会）用の IAM アクセスキー。SMTP 送信認証情報とは別物。
  | 'awsSesAccessKeyId'
  | 'awsSesSecretAccessKey'
  // 掲示板 Phase 5: Chatwork 通知 API トークン。
  | 'chatworkApiToken';

export type SettingsSecretsStatus = Record<SecretKey, SecretStatus>;

// ─────────────────────────────────────────────
// LIFF 設定（Phase 24f / spec_line_bot.md A-12）
// settings/liff ドキュメント。LIFF ID は LIFF URL に含まれる公開情報のため、
// firestore.rules で read=true（公開）, write=false（Cloud Function 経由のみ）。
// ─────────────────────────────────────────────
export interface SettingsLiff {
  liffId: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * Google タグ（GTM 等）のグローバル既定設定 (settings/googleTag)。
 * 全公開ページ（ランディング・公開フォーム）に既定で注入される。
 * ランディング個別設定 (landing.googleTag) があればそちらが優先される。
 * headHtml / bodyHtml は外注の広告代理店から受領した生コードをそのまま保持する。
 */
export interface SettingsGoogleTag {
  /** false の場合は既定タグを注入しない（個別タグには影響しない）。 */
  enabled: boolean;
  /** A タグ: <head> 内に注入する生コード（GTM の inline script を含む）。 */
  headHtml: string;
  /** B タグ: <body> 直下に注入する生コード（<noscript> フォールバック等）。 */
  bodyHtml: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

// ─────────────────────────────────────────────
// 監査ログ / システムログ
// ─────────────────────────────────────────────
export type AuditAction = 'create' | 'update' | 'delete';

export interface SecretAuditLog {
  action: AuditAction;
  secretKey: SecretKey;
  performedBy: string;
  performedByEmail: string;
  ipAddress: string;
  userAgent: string;
  timestamp: IsoOrTimestamp;
}

export interface LiffAuditLog {
  action: AuditAction;
  liffId: string;
  previousLiffId: string | null;
  performedBy: string;
  performedByEmail: string;
  ipAddress: string;
  userAgent: string;
  timestamp: IsoOrTimestamp;
}

export type SystemLogLevel = 'info' | 'warn' | 'error';
export type SystemLogContext =
  | 'line.webhook'
  | 'line.claude'
  | 'line.rag'
  | 'line.notification'
  | 'mail.generate'
  | 'mail.analysis'
  | 'mail.gmail'
  | 'mail.migrate'
  | 'common.auth'
  | 'common.secret'
  | 'common.admin';

export interface SystemLog {
  level: SystemLogLevel;
  context: SystemLogContext;
  message: string;
  metadata: Record<string, unknown>;
  timestamp: IsoOrTimestamp;
}
